import { ActionIcon, Box, Button, Divider, Group, Stack, Switch, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus, IconX } from "@tabler/icons-react";
import { createFileRoute, useLocation, useMatchRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { GroupChildrenRepository, GroupSearchRepository } from "shared/components/organization/groups/data";
import { GroupSelector, sharedOrgPageGroupDisabledCheck } from "shared/components/organization/groups/groups";
import { useCreateGroup } from "shared/graphql/groups";
import { usePermissions } from "shared/stores/oidc";
import { useAside } from "shared/utils/aside";
import { to } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { GroupLikeFragment } from "src/gql/graphql";
import { container } from "tsyringe";

const CreateForm = () => {
    const nav = Route.useNavigate();
    const { permissions } = usePermissions();
    const form = useForm<{ name: string; parents: GroupLikeFragment[]; internalJoinable: boolean }>({
        initialValues: { name: "", parents: [], internalJoinable: false },
        validate: {
            name: (it) => it.length == 0,
            parents: (it) => it.length == 0,
        },
    });
    const { createGroupAsync } = useCreateGroup();
    const [parentSelectorOpened, { toggle: toggleParentSelector }] = useDisclosure();
    const { parents } = form.getValues();

    return (
        <form
            onSubmit={form.onSubmit((values) => {
                void createGroupAsync({
                    name: values.name,
                    parentGroupIds: values.parents.map(to("id")),
                    internalJoinable: values.internalJoinable,
                    members: [],
                }).then(({ createGroup }) => {
                    const groupChildrenRepo = container.resolve(GroupChildrenRepository);
                    const groupSearchRepo = container.resolve(GroupSearchRepository);

                    if (createGroup.id) {
                        groupChildrenRepo.upsert(createGroup);
                        groupSearchRepo.upsert(createGroup);

                        return nav({
                            to: "/organization/groups/$groupId",
                            params: { groupId: createGroup.id },
                        });
                    } else {
                        notify.show.error({ message: "Error creating group" });
                    }
                });
            })}
            style={{ height: "80%", flexGrow: 1 }}
        >
            <Stack mah="100%">
                <TextInput label="Name" placeholder="Enter name..." {...form.getInputProps("name")} />
                <Switch
                    key={form.key("internalJoinable")}
                    label="Joinable"
                    w="fit-content"
                    py="sm"
                    size="sm"
                    {...form.getInputProps("internalJoinable", { type: "checkbox" })}
                />
                <Divider />
                {parentSelectorOpened ? (
                    <Stack>
                        <Group>
                            <Title order={3}>Parent(s)</Title>
                            <Button variant="default" leftSection={<IconX size={16} />} onClick={toggleParentSelector}>
                                Close
                            </Button>
                        </Group>
                        <GroupSelector
                            selectedGroups={form.getValues().parents}
                            onChange={(group, checked) => {
                                form.setValues({
                                    parents: checked
                                        ? form.getValues().parents.concat(group)
                                        : form.getValues().parents.filter((it) => it.id != group.id),
                                });
                            }}
                            shouldDisable={(group) => sharedOrgPageGroupDisabledCheck(permissions, group)}
                        />
                    </Stack>
                ) : (
                    <Stack>
                        <Group>
                            <Title order={3}>Parent(s)</Title>
                            <Button variant="default" leftSection={<IconPlus size={16} />} onClick={toggleParentSelector}>
                                Add
                            </Button>
                        </Group>
                        {form.getValues().parents.map((parent) => (
                            <Group key={parent.id} justify="space-between">
                                <Text>{parent.name}</Text>
                                <ActionIcon
                                    variant="transparent"
                                    onClick={() => form.setValues({ parents: parents.filter((it) => it.id != parent.id) })}
                                    aria-label="Remove parent"
                                >
                                    <IconX size={16} />
                                </ActionIcon>
                            </Group>
                        ))}
                    </Stack>
                )}
                <Box display="flex">
                    <Button leftSection={<IconPlus size={16} />} fullWidth flex={1} type="submit" disabled={!form.isValid()}>
                        Create
                    </Button>
                </Box>
            </Stack>
        </form>
    );
};

function Page() {
    const aside = useAside();
    const nav = Route.useNavigate();
    const { parent } = Route.useSearch();
    const { href } = useLocation();
    const matchRoute = useMatchRoute();

    useEffect(() => {
        if (!matchRoute({ to: "/organization/groups", fuzzy: true })) {
            aside.dismiss({ silent: true });
        }
    }, [href]);

    useEffect(() => {
        aside.present({
            sticky: true,
            label: "Create Group",
            component: <CreateForm />,
            didDismiss: () => nav({ to: "/organization/groups", search: { parent } }),
        });
    }, []);

    return null;
}

/** @public */
export const Route = createFileRoute("/_auth/organization/groups/create")({
    component: Page,
});
